import { useEffect, useMemo, useState } from 'react';
import { Badge, Button } from '@roger/ui-components';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Laptop,
  Printer,
  Smartphone,
  Tablet,
} from 'lucide-react';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { FormValue } from '../../components/oasis/types';
import { BespokeLayoutRenderer } from '../../components/oasis/bespoke/layout/BespokeLayoutRenderer';
import {
  tryParseLayoutJson,
  getLayoutCompileError,
} from '../../components/oasis/bespoke/layout/tryParseLayoutJson';
import { collectSectionCoverageQids } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import {
  formTreeSectionToQuestions,
  type FormTreeQuestionInput,
} from '../../components/oasis/bespoke/layout-studio/formTreeToQuestions';
import {
  collectSectionLayoutCoverageIssues,
  findLayoutIssue,
} from './formSchemaUtils';
import type { FormTreeQuestion } from './formSchemaTypes';
import { SectionLayoutJsonDialog } from './SectionLayoutJsonDialog';
import { PreviewFrame } from './PreviewFrame';
import { PrintPreview } from './PrintPreview';
import {
  PREVIEW_VIEWPORT_LABELS,
  PREVIEW_VIEWPORT_ORDER,
  type PreviewViewport,
} from './previewViewport';

const PREVIEW_VIEWPORT_ICONS: Record<PreviewViewport, typeof Laptop> = {
  laptop: Laptop,
  tablet: Tablet,
  mobile: Smartphone,
  print: Printer,
};

export interface SectionLayoutViewProps {
  /** The section's one layout, or `null` when it has none yet. */
  template: BespokeTemplateResponse | null;
  formType: RogerDocFormType;
  schemaVersion: FormSchemaVersion;
  sectionName: string;
  sectionLabel: string;
  /** The section's questions from the selected schema version's form tree. */
  questions: FormTreeQuestion[];
  layoutJsonOpen: boolean;
  onLayoutJsonOpenChange: (open: boolean) => void;
  /** Owned by the parent so it survives section and form type switches. */
  previewViewport: PreviewViewport;
  onPreviewViewportChange: (viewport: PreviewViewport) => void;
  /** Copies a layout's JSON, with the page's toasts. */
  onCopyLayout: (template: BespokeTemplateResponse | null) => void;
}

/**
 * Read-only view of one section's layout: the validation strip, the interactive
 * preview at each viewport, the print preview and the layout JSON. Layouts are
 * written only through the Forms Manager MCP.
 */
export function SectionLayoutView({
  template,
  formType,
  schemaVersion,
  sectionName,
  sectionLabel,
  questions,
  layoutJsonOpen,
  onLayoutJsonOpenChange,
  previewViewport,
  onPreviewViewportChange,
  onCopyLayout,
}: SectionLayoutViewProps) {
  const [validationOpen, setValidationOpen] = useState(true);
  const [previewAnswers, setPreviewAnswers] = useState<
    Record<string, FormValue>
  >({});

  const layoutJsonText = useMemo(
    () =>
      template?.layout_json == null
        ? ''
        : JSON.stringify(template.layout_json, null, 2),
    [template?.layout_json]
  );
  const sectionQuestions = useMemo(
    () =>
      formTreeSectionToQuestions(
        questions as unknown as FormTreeQuestionInput[]
      ),
    [questions]
  );

  const parsedLayout = useMemo(
    () => tryParseLayoutJson(layoutJsonText),
    [layoutJsonText]
  );

  // A pushed layout is a new form: clear answers typed into the old one.
  useEffect(() => {
    setPreviewAnswers({});
  }, [layoutJsonText]);

  const sectionCoverageQids = useMemo(
    () => collectSectionCoverageQids(sectionQuestions),
    [sectionQuestions]
  );
  const layoutCoverageIssues = useMemo(() => {
    if (!parsedLayout.ok || sectionQuestions.length === 0) {
      return null;
    }
    return collectSectionLayoutCoverageIssues(
      parsedLayout.layout,
      sectionQuestions as FormTreeQuestion[]
    );
  }, [parsedLayout, sectionQuestions]);

  /**
   * Every problem with the layout, straight from the validator. Nothing here
   * names an issue kind, so a rule added to the validator shows up unwired.
   */
  const coverageIssues = layoutCoverageIssues?.parseOk
    ? layoutCoverageIssues.issues
    : [];

  // The one place a single kind is asked for by name: the Fields n/N badge
  // counts coverage specifically, not problems in general.
  const uncoveredCount = layoutCoverageIssues?.parseOk
    ? (findLayoutIssue(layoutCoverageIssues, 'uncovered-qids')?.items.length ??
      0)
    : null;
  const targetN = sectionCoverageQids.length;

  const layoutCompileError = useMemo(
    () =>
      layoutJsonText.trim() === ''
        ? null
        : getLayoutCompileError(layoutJsonText),
    [layoutJsonText]
  );

  const hasLayout = layoutJsonText.trim() !== '';
  const layoutEmpty =
    !hasLayout || (parsedLayout.ok && parsedLayout.layout.length === 0);
  const validationPassing =
    hasLayout &&
    parsedLayout.ok &&
    sectionQuestions.length > 0 &&
    coverageIssues.length === 0;

  const formSchemaVersion = schemaVersion.version?.trim() ?? '';

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='pb-3'>
          <h2 className='truncate text-lg font-semibold' title={sectionName}>
            {sectionLabel}
          </h2>
          <p className='text-xs text-muted-foreground'>{schemaVersion.name}</p>
        </div>

        {layoutCompileError && (
          <div
            role='alert'
            className='mb-3 flex gap-2 border-l-2 border-destructive px-3 py-2'
          >
            <AlertTriangle
              className='mt-0.5 h-4 w-4 shrink-0 text-destructive'
              aria-hidden
            />
            <div className='min-w-0 text-xs'>
              <p className='font-medium text-destructive'>
                Layout does not compile
              </p>
              <p className='mt-1 text-destructive/90'>{layoutCompileError}</p>
            </div>
          </div>
        )}

        {/* Validation — occupies the visit page's Live Scrubber slot. */}
        <div className='mb-3 border-y border-border'>
          <button
            type='button'
            onClick={() => setValidationOpen(v => !v)}
            aria-expanded={validationOpen}
            className='flex w-full min-w-0 flex-wrap items-center gap-2 py-3 text-left text-sm'
          >
            {validationPassing ? (
              <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
            ) : (
              <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600' />
            )}
            <span className='font-medium'>Validation</span>
            {uncoveredCount != null && targetN > 0 && (
              <Badge
                variant='outline'
                className='whitespace-nowrap text-xs font-mono'
              >
                Fields {targetN - uncoveredCount}/{targetN}
              </Badge>
            )}
            {/* Uncovered QIDs are already the Fields n/N badge. */}
            {coverageIssues
              .filter(issue => issue.kind !== 'uncovered-qids')
              .map(issue => (
                <Badge
                  key={issue.kind}
                  variant='destructive'
                  className='whitespace-nowrap text-xs font-mono'
                  title={issue.error}
                >
                  {issue.label} {issue.count}
                </Badge>
              ))}
            {validationOpen ? (
              <ChevronUp className='ml-auto h-4 w-4 shrink-0 text-muted-foreground' />
            ) : (
              <ChevronDown className='ml-auto h-4 w-4 shrink-0 text-muted-foreground' />
            )}
          </button>
          {validationOpen && (
            <div className='max-h-56 space-y-3 overflow-y-auto border-t border-border py-3'>
              {sectionQuestions.length === 0 ? (
                <p className='text-xs text-muted-foreground'>
                  This section has no questions.
                </p>
              ) : !hasLayout ? (
                <p className='text-xs text-muted-foreground'>
                  This section has no custom layout.
                </p>
              ) : coverageIssues.length === 0 && parsedLayout.ok ? (
                <p className='text-xs text-muted-foreground'>
                  No problems found.
                </p>
              ) : null}
              {/* Each issue carries its own heading, tone and layout. */}
              {coverageIssues.map(issue => (
                <div key={issue.kind} className='text-xs'>
                  <p
                    className={
                      issue.tone === 'destructive'
                        ? 'font-medium text-destructive'
                        : 'font-medium text-amber-800 dark:text-amber-400'
                    }
                  >
                    {issue.title} ({issue.count})
                  </p>
                  {issue.display === 'inline' ? (
                    <pre className='mt-1 max-h-32 overflow-auto bg-muted/40 p-2 text-[11px] leading-tight whitespace-pre-wrap'>
                      {issue.items.join(', ')}
                    </pre>
                  ) : (
                    <ul className='mt-1 max-h-40 list-inside list-disc space-y-1 overflow-auto bg-muted/40 p-2 text-[11px] leading-tight'>
                      {issue.items.map((item, i) => (
                        <li key={`${issue.kind}-${i}`}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='relative flex min-h-[28rem] flex-1 flex-col'>
          <div className='flex shrink-0 flex-wrap items-center justify-end gap-1 pt-2'>
            {PREVIEW_VIEWPORT_ORDER.map(vp => {
              const Icon = PREVIEW_VIEWPORT_ICONS[vp];
              return (
                <Button
                  key={vp}
                  type='button'
                  variant={previewViewport === vp ? 'secondary' : 'ghost'}
                  size='sm'
                  className='gap-1'
                  aria-pressed={previewViewport === vp}
                  title={`Preview at ${PREVIEW_VIEWPORT_LABELS[vp]} width`}
                  onClick={() => onPreviewViewportChange(vp)}
                >
                  <Icon className='h-3.5 w-3.5' aria-hidden />
                  {PREVIEW_VIEWPORT_LABELS[vp]}
                </Button>
              );
            })}
          </div>
          <div className='min-h-0 flex-1 overflow-auto py-3'>
            {previewViewport === 'print' ? (
              formSchemaVersion === '' ? (
                <p className='p-6 text-sm text-muted-foreground'>
                  This schema version has no version number to print.
                </p>
              ) : (
                <PrintPreview
                  formType={formType}
                  schemaVersionName={schemaVersion.name}
                  formSchemaVersion={formSchemaVersion}
                  sectionName={sectionName}
                  layout={
                    parsedLayout.ok
                      ? (parsedLayout.layout as unknown as Record<
                          string,
                          unknown
                        >[])
                      : null
                  }
                />
              )
            ) : (
              <PreviewFrame
                viewport={previewViewport}
                containerClassName='relative min-h-full py-3'
              >
                {sectionQuestions.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    This section has no questions to preview.
                  </p>
                ) : layoutEmpty ? (
                  <div className='flex min-h-[24rem] items-center justify-center'>
                    <p className='text-sm text-muted-foreground/70'>
                      No custom layout for this section
                    </p>
                  </div>
                ) : layoutCompileError ? (
                  <div className='flex min-h-[24rem] flex-col items-center justify-center gap-2 px-6 text-center'>
                    <p className='text-sm font-medium text-destructive'>
                      Layout does not compile
                    </p>
                    <p className='max-w-md text-sm text-muted-foreground'>
                      {layoutCompileError}
                    </p>
                  </div>
                ) : parsedLayout.ok ? (
                  <BespokeLayoutRenderer
                    layout={parsedLayout.layout}
                    answers={previewAnswers}
                    onAnswerChange={(qid, value) =>
                      setPreviewAnswers(prev => ({ ...prev, [qid]: value }))
                    }
                    onFormDataChange={() => {}}
                    schema={sectionQuestions}
                    schemaQuestionOptions={[]}
                    sectionName={sectionName}
                  />
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    The stored Roger Layout Schema is not valid JSON.
                  </p>
                )}
              </PreviewFrame>
            )}
          </div>
        </div>
      </div>

      <SectionLayoutJsonDialog
        open={layoutJsonOpen}
        onOpenChange={onLayoutJsonOpenChange}
        sectionDisplayName={sectionLabel}
        value={layoutJsonText}
        onCopyLayoutJson={() => onCopyLayout(template)}
      />
    </>
  );
}
